import { getActiveInteraction } from '@atlaskit/react-ufo/interaction-metrics';

import { fg } from '@confluence/feature-gating';

import { getLoadedBylineMetrics, loadBylineMetrics } from './loadBylineMetrics.ts';

export type InitialSnapshotOutcome =
	| 'unknown'
	| 'notApplicable'
	| 'disabled'
	| 'available'
	| 'empty'
	| 'incomplete'
	| 'failed';

type PresentationSource = 'resolver' | 'contentProperty';
export type PresentationObservation = {
	source: PresentationSource;
	outcome: 'succeeded' | 'failed';
	titleChanged: boolean;
	iconChanged: boolean;
};

// Call only from effects/callbacks. Capturing once before an await binds its result to that load.
export type BylineLifecycle = {
	snapshot: (outcome: InitialSnapshotOutcome, resolvedCount: number | null) => void;
	runtimeMounted: () => void;
	snapshotSuppressed: () => void;
	presentation: (observation: PresentationObservation) => void;
	cancel?: () => void;
} | null;

export const captureBylineLifecycle = (): BylineLifecycle => {
	if (!fg('confluence_byline_cls_telemetry')) return null;
	const interaction = getActiveInteraction();
	if (
		!interaction ||
		interaction.end ||
		(interaction.type !== 'page_load' && interaction.type !== 'transition')
	)
		return null;
	const loaded = getLoadedBylineMetrics();
	if (loaded) return loaded.captureBylineLifecycle(interaction);

	// Buffer only bounded numeric observations, preserving their original timestamps and load.
	let recorder: BylineLifecycle = null;
	let cancelled = false;
	let time = 0;
	const queue: { write: (recorder: NonNullable<BylineLifecycle>) => void; time: number }[] = [];
	const write = (update: (recorder: NonNullable<BylineLifecycle>) => void) => {
		if (
			cancelled ||
			getActiveInteraction() !== interaction ||
			interaction.end ||
			!fg('confluence_byline_cls_telemetry')
		)
			return;
		time = performance.now();
		if (recorder) update(recorder);
		else if (queue.length < 50) queue.push({ write: update, time });
	};
	void loadBylineMetrics().then((metrics) => {
		if (
			!cancelled &&
			metrics &&
			getActiveInteraction() === interaction &&
			!interaction.end &&
			fg('confluence_byline_cls_telemetry')
		) {
			recorder = metrics.captureBylineLifecycle(interaction, () => time);
			if (recorder) {
				for (const observation of queue) {
					time = observation.time;
					observation.write(recorder);
				}
			}
		}
		queue.length = 0;
		if (!recorder) cancelled = true;
	});
	return {
		cancel: () => {
			cancelled = true;
			queue.length = 0;
		},
		snapshot: (outcome, count) => write((r) => r.snapshot(outcome, count)),
		runtimeMounted: () => write((r) => r.runtimeMounted()),
		snapshotSuppressed: () => write((r) => r.snapshotSuppressed()),
		presentation: (observation) => write((r) => r.presentation(observation)),
	};
};
